import { useState, useCallback, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Coins } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Card, Combobox, Input, Tabs, Toggle,
} from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { currencyApi }         from '@/api/currency.api';
import type { CurrencyDto, CurrencyListDto } from '@/types/currency.types';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  code:          z.string().min(1, 'Code is required')
                   .length(3, 'Currency code must be exactly 3 characters')
                   .regex(/^[A-Za-z]+$/, 'Only letters allowed'),
  name:          z.string().min(1, 'Name is required').max(100, 'Max 100 characters'),
  symbol:        z.string().min(1, 'Symbol is required').max(10, 'Max 10 characters'),
  decimalPlaces: z.coerce.number({ invalid_type_error: 'Decimal places is required' })
                   .int('Decimal places must be a whole number')
                   .min(0, 'Decimal places cannot be negative')
                   .max(4, 'Decimal places cannot be more than 4'),
  isActive:      z.boolean(),
});

type FormValues = z.infer<typeof schema>;

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_FORM: FormValues = {
  code: '', name: '', symbol: '', decimalPlaces: 2, isActive: true,
};

const DECIMAL_PLACES = ['No decimals', '1 decimal', '2 decimals (standard)', '3 decimals', '4 decimals']
  .map((label, n) => ({ value: String(n), label: `${n} — ${label}` }));

const TABS = ['General', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<CurrencyListDto>[] = [
  { key: 'code',          label: 'Code',     className: 'w-16 font-mono' },
  { key: 'name',          label: 'Name' },
  { key: 'symbol',        label: 'Symbol',   className: 'w-16 text-center' },
  { key: 'decimalPlaces', label: 'Decimals', className: 'w-20 text-center' },
  { key: 'isActive', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function dtoToForm(dto: CurrencyDto): FormValues {
  return {
    code:          dto.code,
    name:          dto.name,
    symbol:        dto.symbol,
    decimalPlaces: dto.decimalPlaces,
    isActive:      dto.isActive,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CurrencyMasterPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_CURRENCY_VIEW);
  const canCreate = hasPermission(Permissions.MST_CURRENCY_CREATE);
  const canEdit   = hasPermission(Permissions.MST_CURRENCY_EDIT);
  const canDelete = hasPermission(Permissions.MST_CURRENCY_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const codeInputRef = useRef<HTMLInputElement>(null);

  // ── Local state ──────────────────────────────────────────────────────────────
  const [codeInput,    setCodeInput]    = useState('');

  const [showNewConfirm,    setShowNewConfirm]     = useState<string | null>(null);

  // ── Form ─────────────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const isActive = watch('isActive');

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const master = useMasterRecord<CurrencyDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: codeInputRef,
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => { setCodeInput(''); },
    onDelete: {
      remove:   id => currencyApi.remove(id),
      describe: r => `Currency '${r.code} – ${r.name}'`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: CurrencyDto) => {
    setRecord(dto);
    reset(dtoToForm(dto));
    setCodeInput(dto.code);
    screen.setMode('view');
    setActiveTab('General');
  }, [reset, screen]);

  // ── Code lookup ───────────────────────────────────────────────────────────────
  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;

    if (!canView) {
      flash('error', 'You do not have permission to view currencies.');
      return;
    }

    setLookupBusy(true);
    try {
      const res = await currencyApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) {
          flash('error', `Currency code '${code}' was not found.`);
          return;
        }
        setShowNewConfirm(code);
      } else {
        flash('error', 'Lookup failed. Please try again.');
      }
    } finally {
      setLookupBusy(false);
    }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm;
    setShowNewConfirm(null);
    if (!code) return;
    reset({ ...EMPTY_FORM, code });
    setCodeInput(code);
    setRecord(null);
    screen.setMode('new');
    setActiveTab('General');
  };

  // ── Toolbar handlers ──────────────────────────────────────────────────────────

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false);
    setSaving(true);
    try {
      const data = pendingSave;
      if (screen.isNew) {
        await currencyApi.create({
          code:          data.code.toUpperCase(),
          name:          data.name.trim(),
          symbol:        data.symbol.trim(),
          decimalPlaces: data.decimalPlaces,
        });
        flash('success', 'Currency created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await currencyApi.update(record.id, {
          name:          data.name.trim(),
          symbol:        data.symbol.trim(),
          decimalPlaces: data.decimalPlaces,
          isActive:      data.isActive,
        });
        flash('success', 'Currency updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed. Please try again.';
      flash('error', msg);
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };



  // ── Search modal ──────────────────────────────────────────────────────────────
  const fetchCurrencies = useMemo(() => masterListFetcher(currencyApi.getList), []);

  const handleSearchSelect = (item: CurrencyListDto) => {
    setSearchOpen(false);
    setLookupBusy(true);
    currencyApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load currency.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">

      <MasterPageHeader icon={Coins} title="Currency Maintenance"
        subtitle="Manage ISO 4217 currency codes and symbols" screen={screen} />

      {/* ── Toolbar ── */}
      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      {/* ── Flash message ── */}
      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Code"
        summary={record && (<>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">
              {record.name}
            </span>
            <span className="text-xs text-gray-400 font-mono">{record.symbol}</span>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wide">
                Inactive
              </span>
            )}
        </>)}
        hint={screen.isInitial ? "Enter a 3-letter ISO code and press Enter, or click the search icon to browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Browse currencies"
          placeholder="Type ISO code + Enter…"
          maxLength={3}
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      {/* ── Main content — ALWAYS visible ── */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>

          {/* Tabs */}
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {/* ── General ── */}
            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  Currency Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Code — always read-only in form */}
                  <Input
                    label="Currency Code (ISO 4217)"
                    {...register('code')}
                    disabled
                    placeholder="— enter via code field above —"
                    className="font-mono uppercase"
                    hint={screen.isNew ? '3-letter ISO 4217 code. Cannot be changed after creation.' : undefined}
                  />

                  <Input
                    label="Currency Name *"
                    {...register('name')}
                    error={errors.name?.message}
                    disabled={fieldsDisabled}
                    placeholder="e.g. US Dollar"
                  />

                  <Input
                    label="Symbol *"
                    {...register('symbol')}
                    error={errors.symbol?.message}
                    disabled={fieldsDisabled}
                    placeholder="e.g. $, €, £, RM"
                    hint="Display symbol used on amounts."
                  />

                  <Controller name="decimalPlaces" control={control} render={({ field }) => (
                    <Combobox
                      label="Decimal Places"
                      options={DECIMAL_PLACES}
                      clearable={false}
                      value={String(field.value)}
                      onChange={v => field.onChange(Number(v))}
                      disabled={fieldsDisabled}
                      error={errors.decimalPlaces?.message}
                    />
                  )} />

                  {/* Status toggle — only shown in EDIT mode */}
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <Toggle
                        label={isActive ? 'Active' : 'Inactive'}
                        checked={isActive}
                        onChange={v => setValue('isActive', v, { shouldDirty: true })}
                        disabled={fieldsDisabled}
                      />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* ── Audit ── */}
            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      {/* ════ Modals ════ */}

      <MasterSearchModal<CurrencyListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Browse Currencies"
        columns={SEARCH_COLUMNS}
        fetchData={fetchCurrencies}
        onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Currency"
        screen={screen}
        record={record}
        describe={r => `${r.code} — ${r.name}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title="Currency Master Report"
        >
          <ReportHeader
            reportTitle="Currency Master Report"
            printedBy={user?.fullName ?? user?.username}
            printedAt={new Date()}
            meta={[
              { label: 'Currency Code', value: record.code },
              { label: 'Status',        value: record.isActive ? 'Active' : 'Inactive' },
            ]}
          />

          <ReportSection
            title="Currency Details"
            fields={[
              { label: 'Currency Code',  value: record.code },
              { label: 'Currency Name',  value: record.name, wide: true },
              { label: 'Symbol',         value: record.symbol },
              { label: 'Decimal Places', value: record.decimalPlaces },
              { label: 'Status',         value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
            ]}
          />

          <ReportSection
            title="Audit Information"
            fields={[
              { label: 'Created By',    value: record.createdByName ?? record.createdBy },
              { label: 'Created Date',  value: formatDateTime(record.createdDate) },
              { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
              { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
            ]}
          />

          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
